import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { ArrowLeft, MoreVertical, Users, AlertTriangle, CheckCircle2, Clock, Edit, Trash2, UserPlus, Mail, Download, Pause, Play, TrendingUp } from 'lucide-react';
import { StaticMetricCardAlt } from '@/components/StaticMetricCardAlt';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DemoModeBadge } from '@/components/ui/demo-mode-badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/audits/StatusBadge';
import { PriorityIndicator } from '@/components/audits/PriorityIndicator';
import { EditAuditDialog } from '@/components/audits/EditAuditDialog';
import { ExportOptionsDialog } from '@/components/audits/ExportOptionsDialog';
import { AssetProgressTab } from '@/components/audits/AssetProgressTab';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import { auditDataService } from '@/services/audit-data-service';
import type { Audit, AuditException, AuditActivityLog } from '@/types/asset-audit';
import { AuditStatus, ExceptionStatus } from '@/types/asset-audit';
import type { AssetException, ExceptionSummary } from '@/types/exception';
import { format } from 'date-fns';
import { getActivityTypeLabel } from '@/utils/activity-type-labels';

// Helper function to generate exception ID - extracted to reduce cognitive complexity (S3776)
function generateExceptionId(auditException: AuditException): string {
  return `EXC-${new Date(auditException.reportedDate).getFullYear()}-${auditException.id.slice(0, 6)}`;
}

// Helper function to calculate days open - extracted to reduce cognitive complexity (S3776)
function calculateDaysOpen(reportedDate: string): number {
  return Math.floor((new Date().getTime() - new Date(reportedDate).getTime()) / (1000 * 60 * 60 * 24));
}

// Helper function to check if exception is overdue - extracted to reduce cognitive complexity (S3776)
function isExceptionOverdue(followUpDueDate?: string): boolean {
  return followUpDueDate ? new Date(followUpDueDate) < new Date() : false;
}

// Helper function to get due date display value - extracted to fix nested ternary (S3358)
function getDueDateDisplayValue(isOverdue: boolean, daysRemaining: number): string {
  if (isOverdue) {
    return 'Overdue';
  }
  if (daysRemaining === 0) {
    return 'Due Today';
  }
  if (daysRemaining === 1) {
    return 'Due Tomorrow';
  }
  return `Due in ${daysRemaining}d`;
}

// Helper function to get due date change label - extracted to fix nested ternary (S3358)
function getDueDateChangeLabel(isOverdue: boolean, daysRemaining: number): string {
  if (isOverdue) {
    return `${Math.abs(daysRemaining)} days overdue`;
  }
  if (daysRemaining === 0) {
    return 'Complete today';
  }
  if (daysRemaining === 1) {
    return 'Complete tomorrow';
  }
  return 'On track';
}

// Helper function to convert AuditException to ExceptionSummary - uses extracted helpers to reduce cognitive complexity (S3776)
const convertToExceptionSummary = (auditException: AuditException): ExceptionSummary => {
  return {
    id: auditException.id,
    exceptionId: generateExceptionId(auditException),
    status: auditException.status as unknown as import('@/types/exception').ExceptionStatus,
    priority: auditException.severity as unknown as import('@/types/exception').ExceptionPriority,
    exceptionType: auditException.type as unknown as import('@/types/exception').ExceptionType ?? 'other',
    assetTag: auditException.assetCode,
    assetName: auditException.assetName ?? 'Unknown Asset',
    description: auditException.notes ?? 'No description provided',
    discoveryDate: auditException.reportedDate,
    assignedToName: auditException.assignedToName ?? '',
    workItemsCount: 0,
    workItemsCompleted: 0,
    commentsCount: 0,
    daysOpen: calculateDaysOpen(auditException.reportedDate),
    isOverdue: isExceptionOverdue(auditException.followUpDueDate),
  };
};

// Helper function to convert AuditException to AssetException
const convertToAssetException = (auditException: AuditException): AssetException => {
  return {
    id: auditException.id,
    exceptionId: `EXC-${new Date(auditException.reportedDate).getFullYear()}-${auditException.id.slice(0, 6)}`,
    status: auditException.status as unknown as import('@/types/exception').ExceptionStatus,
    priority: auditException.severity as unknown as import('@/types/exception').ExceptionPriority,
    createdDate: auditException.reportedDate,
    lastUpdated: auditException.reportedDate,
    assetId: auditException.assetId,
    assetTag: auditException.assetCode,
    assetName: auditException.assetName ?? 'Unknown Asset',
    assetCategory: 'IT Equipment',
    serialNumber: '',
    manufacturer: '',
    model: '',
    currentBookValue: 0,
    purchaseDate: auditException.reportedDate,
    depreciationStatus: 'Active',
    department: '',
    building: auditException.location ?? 'Unknown',
    floor: '',
    room: '',
    currentCustodian: auditException.reportedByName ?? 'Unknown',
    lastKnownLocation: auditException.location,
    exceptionType: auditException.type as unknown as import('@/types/exception').ExceptionType ?? 'other',
    description: auditException.notes ?? 'No description provided',
    deviationFromStandard: '',
    financialImpact: 0,
    operationalImpact: 'Medium',
    complianceRiskLevel: 'medium',
    discoveredBy: auditException.reportedBy ?? 'Unknown',
    discoveredByName: auditException.reportedByName ?? 'Unknown',
    discoveryDate: auditException.reportedDate,
    auditReference: auditException.auditNumber ?? '',
    discoveryMethod: 'physical-audit',
    documents: [],
    comments: [],
    rootCause: 'under-investigation',
    contributingFactors: [],
    rootCauseNarrative: '',
    preventiveRecommendations: '',
    policyChangesRequired: '',
    trainingNeeds: '',
    systemImprovements: '',
    assignedTo: auditException.assignedTo ?? '',
    assignedToName: auditException.assignedToName ?? '',
    assignedDate: auditException.reportedDate,
    targetResolutionDate: auditException.followUpDueDate ?? '',
    actualResolutionDate: undefined,
    escalationLevel: undefined,
    workItems: [],
    estimatedLoss: 0,
    recoveryAmount: 0,
    adjustmentRequired: false,
    insuranceClaimStatus: undefined,
    budgetCodeAffected: '',
    regulatoryFrameworks: [],
    complianceRiskRating: 'medium',
    auditStandardReference: '',
    reportingRequirements: '',
    externalReportingStatus: undefined,
    resolutionType: undefined,
    resolutionDescription: '',
    verifiedBy: undefined,
    verificationDate: undefined,
    managementApproval: false,
    followUpDate: auditException.followUpDueDate ?? undefined,
    activityLog: [],
    relatedExceptions: [],
    createdBy: auditException.reportedBy ?? 'Unknown',
  };
};

export default function AuditDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { modules } = useFeatureSettings();
  const auditsModule = modules?.audits;
  const [audit, setAudit] = useState<Audit | null>(null);
  const [_auditItems, setAuditItems] = useState<any[]>([]);
  const [exceptions, setExceptions] = useState<AuditException[]>([]);
  const [activityLog, setActivityLog] = useState<AuditActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedException, setSelectedException] = useState<AuditException | null>(null);
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);

  useEffect(() => {
    loadAuditDetails();
  }, [id]);

  const loadAuditDetails = async () => {
    setLoading(true);
    try {
      if (!id) return;

      // Load audit details
      const foundAudit = await auditDataService.getAuditById(id);

      if (foundAudit) {
        setAudit(foundAudit);
        
        // Load audit items (assets in the audit)
        const itemsResult = await auditDataService.getAuditItems(id, 1, 1000);
        setAuditItems(itemsResult?.items ?? []);
        
        // Load exceptions for this audit
        const exceptionsResult = await auditDataService.getExceptions({
          auditIds: [id],
          page: 1,
          pageSize: 100,
        });
        setExceptions(exceptionsResult?.items ?? []);

        // Load activity log
        const activityResult = await auditDataService.getActivityLogs(id, 1, 50);
        setActivityLog(activityResult?.items ?? []);
      }
    } catch (error) {
      console.error('Error loading audit details:', error);
    } finally {
      setLoading(false);
    }
  };

    const _handleStatusChange = async (newStatus: AuditStatus) => {
      if (!audit) return;
      try {
        setAudit(prev => prev ? { ...prev, status: newStatus } : null);
        toast({
          title: "Status Updated",
          description: `Audit status changed to ${newStatus}.`,
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to update status. Please try again.",
          variant: "destructive",
        });
      }
    };

  const handleEdit = () => {
    setShowEditDialog(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleContactAssignees = async () => {
    try {
      toast({
        title: "Notification Sent",
        description: "All assignees have been notified.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send notifications. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditAudit = async (updatedAudit: Partial<Audit>) => {
    try {
      setAudit(prev => prev ? { ...prev, ...updatedAudit } as Audit : null);
      toast({
        title: "Audit Updated",
        description: "The audit has been successfully updated.",
      });
      setShowEditDialog(false);
      loadAuditDetails(); // Reload to get fresh data
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update audit. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAudit = async () => {
    try {
      // In real app, call API to delete audit
      toast({
        title: "Audit Deleted",
        description: "The audit has been successfully deleted.",
      });
      navigate('/audits');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete audit. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePauseAudit = async () => {
    try {
      setAudit(prev => prev ? { ...prev, status: AuditStatus.OnHold } : null);
      toast({
        title: "Audit Paused",
        description: "The audit has been paused.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to pause audit. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResumeAudit = async () => {
    try {
      setAudit(prev => prev ? { ...prev, status: AuditStatus.InProgress } : null);
      toast({
        title: "Audit Resumed",
        description: "The audit has been resumed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resume audit. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleViewException = (exception: AuditException) => {
    setSelectedException(exception);
    setShowExceptionDialog(true);
  };

    const _handleResolveException = async (exceptionId: string, resolution: { notes: string; action?: string }) => {
      try {
        await auditDataService.resolveException(exceptionId, {
          notes: resolution.notes,
          resolvedBy: 'current-user', // TODO: Get from auth context
          action: resolution.action,
        });
        await loadAuditDetails();
        toast({
          title: "Exception Resolved",
          description: "The exception has been successfully resolved.",
        });
      } catch (error) {
        console.error('Failed to resolve exception:', error);
        throw error;
      }
    };

  const _handleExceptionStatusChange = async (exceptionId: string, status: ExceptionStatus) => {
    try {
      // TODO: Implement status change API call
      console.log('Changing exception status:', exceptionId, status);
      await loadAuditDetails();
      toast({
        title: "Status Updated",
        description: "Exception status has been updated.",
      });
    } catch (error) {
      console.error('Failed to change exception status:', error);
      throw error;
    }
  };

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      toast({
        title: "Export Started",
        description: `Generating ${format.toUpperCase()} report...`,
      });
      setShowExportDialog(false);
      
      // Simulate export completion
      setTimeout(() => {
        toast({
          title: "Export Complete",
          description: `Your ${format.toUpperCase()} report is ready for download.`,
        });
      }, 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export audit. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Audit Not Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              The audit you're looking for doesn't exist or has been deleted.
            </p>
            <Button onClick={() => navigate('/audits/active')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Audits
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const completedAssets = audit.completedAssets;
  const totalAssets = audit.totalAssets;
  const progressPercentage = audit.completionPercentage;
  const daysRemaining = audit.daysRemaining ?? 0;
  const isOverdue = audit.isOverdue;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card px-6 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/audits/active')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl font-bold">{audit.title}</h1>
              {auditsModule?.mode === 'demo' && <DemoModeBadge />}
              <StatusBadge status={audit.status} />
              <PriorityIndicator priority={audit.priority} />
            </div>
            <div className="ml-12 text-sm text-muted-foreground">
              Assigned to {audit.assignedUsers?.map(a => a.userName).join(', ') || 'Unassigned'}
              {audit.lastModifiedDate && (
                <> • Last Updated {format(new Date(audit.lastModifiedDate), 'MMM d, yyyy')} at {format(new Date(audit.lastModifiedDate), 'h:mm a')}</>
              )}
              {audit.scopeDetails?.locations && audit.scopeDetails.locations.length > 0 && (
                <> • Location {audit.scopeDetails.locations.join(', ')}</>
              )}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Audit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleContactAssignees}>
                <Mail className="mr-2 h-4 w-4" />
                Contact Assignees
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowExportDialog(true)}>
                <Download className="mr-2 h-4 w-4" />
                Export Report
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {audit.status === AuditStatus.InProgress && (
                <DropdownMenuItem onClick={handlePauseAudit}>
                  <Pause className="mr-2 h-4 w-4" />
                  Pause Audit
                </DropdownMenuItem>
              )}
              {(audit.status === AuditStatus.Assigned || audit.status === AuditStatus.OnHold) && (
                <DropdownMenuItem onClick={handleResumeAudit}>
                  <Play className="mr-2 h-4 w-4" />
                  {audit.status === AuditStatus.OnHold ? 'Resume Audit' : 'Start Audit'}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Audit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Tabs Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            {/* Tab Bar - Enhanced Enterprise Design */}
            <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
              <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
                <TabsTrigger 
                  value="overview"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="progress"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Progress
                </TabsTrigger>
                <TabsTrigger 
                  value="exceptions"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Exceptions
                  {exceptions.length > 0 && (
                    <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                      {exceptions.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="activity"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Activity
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Content */}
            {/* Progress Tab - Full height with its own scroll handling, no padding */}
            <TabsContent value="progress" className="flex-1 overflow-hidden m-0 p-0 data-[state=inactive]:hidden">
              <AssetProgressTab auditId={id!} />
            </TabsContent>

            {/* Other tabs with padding and scrolling */}
            <div className="flex-1 overflow-y-auto data-[state=active]:hidden" data-state={activeTab === 'progress' ? 'active' : 'inactive'}>
              <div className="p-6">

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StaticMetricCardAlt
              title="Progress"
              value={`${progressPercentage}%`}
              change={progressPercentage === 100 ? "✓" : "→"}
              changeLabel={progressPercentage === 100 ? "Audit complete" : "Audit in progress"}
              icon={TrendingUp}
              variant="info"
            />

            <StaticMetricCardAlt
              title="Assets Completed"
              value={`${completedAssets}/${totalAssets}`}
              change={completedAssets === totalAssets ? "✓" : "→"}
              changeLabel={completedAssets === totalAssets ? "All assets verified" : "Verification ongoing"}
              icon={CheckCircle2}
              variant="info"
            />

            <StaticMetricCardAlt
              title="Exceptions"
              value={exceptions.length.toString()}
              change={exceptions.length.toString()}
              changeLabel="Requiring attention"
              icon={AlertTriangle}
              variant="info"
            />

            <StaticMetricCardAlt
              title="Due Date"
              value={getDueDateDisplayValue(isOverdue, daysRemaining)}
              change={audit.dueDate ? format(new Date(audit.dueDate), 'MMM dd, yyyy') : 'Not set'}
              changeLabel={getDueDateChangeLabel(isOverdue, daysRemaining)}
              icon={Clock}
              variant="info"
            />

            <StaticMetricCardAlt
              title="Assignees"
              value={(audit.assignedUsers ?? [])[0]?.userName ?? 'Unassigned'}
              change={(audit.assignedUsers ?? []).length.toString()}
              changeLabel="Team members assigned"
              icon={Users}
              variant="info"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              {/* Audit Information */}
              <Card className="border">
                <CardHeader>
                  <CardTitle>Audit Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Audit Type</div>
                      <div className="text-sm capitalize">{audit.type}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Priority</div>
                      <div className="flex items-center gap-2">
                        <PriorityIndicator priority={audit.priority} size="sm" />
                        <span className="text-sm capitalize">{audit.priority}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Start Date</div>
                      <div className="text-sm">{audit.scheduledStartDate ? format(new Date(audit.scheduledStartDate), 'MMM d, yyyy') : 'Not set'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Due Date</div>
                      <div className="text-sm">{audit.scheduledEndDate ? format(new Date(audit.scheduledEndDate), 'MMM d, yyyy') : 'Not set'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Created By</div>
                      <div className="text-sm">{audit.createdByName}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Status</div>
                      <StatusBadge status={audit.status} />
                    </div>
                  </div>

                  {audit.description && (
                    <>
                      <Separator />
                      <div>
                        <div className="text-sm font-medium text-muted-foreground mb-2">Description</div>
                        <div className="text-sm">{audit.description}</div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Scope Details */}
              <Card className="border">
                <CardHeader>
                  <CardTitle>Scope Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Total Assets</div>
                      <div className="text-2xl font-bold">{audit.totalAssets}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Assets Audited</div>
                      <div className="text-2xl font-bold">{audit.completedAssets}</div>
                    </div>
                  </div>

                  {audit.scopeDetails && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        {audit.scopeDetails.locations && audit.scopeDetails.locations.length > 0 && (
                          <div>
                            <div className="text-sm font-medium text-muted-foreground mb-1">Locations</div>
                            <div className="flex flex-wrap gap-2">
                              {audit.scopeDetails.locations.map((location) => (
                                <Badge key={`location-${location}`} variant="secondary">{location}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {audit.scopeDetails.categories && audit.scopeDetails.categories.length > 0 && (
                          <div>
                            <div className="text-sm font-medium text-muted-foreground mb-1">Categories</div>
                            <div className="flex flex-wrap gap-2">
                              {audit.scopeDetails.categories.map((category) => (
                                <Badge key={`category-${category}`} variant="secondary">{category}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Progress Summary */}
              <Card className="border">
                <CardHeader>
                  <CardTitle>Progress Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Overall Progress</span>
                        <span className="font-medium">{progressPercentage}%</span>
                      </div>
                      <Progress value={progressPercentage} className="h-3" />
                    </div>

                    <div className="grid grid-cols-3 gap-4 pt-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{completedAssets}</div>
                        <div className="text-xs text-muted-foreground">Completed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{totalAssets - completedAssets}</div>
                        <div className="text-xs text-muted-foreground">Remaining</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">{exceptions.length}</div>
                        <div className="text-xs text-muted-foreground">Exceptions</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - 1/3 width */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card className="border">
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button className="w-full justify-start" variant="outline" onClick={handleEdit}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Audit
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={handleContactAssignees}>
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Assignees
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add Assignees
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setShowExportDialog(true)}>
                    <Download className="mr-2 h-4 w-4" />
                    Export Report
                  </Button>
                </CardContent>
              </Card>

              {/* Assignees */}
              <Card className="border">
                <CardHeader>
                  <CardTitle>Assignees ({(audit.assignedUsers ?? []).length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(audit.assignedUsers ?? []).map((assignee) => (
                      <div key={assignee.userId} className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={assignee.userAvatar} />
                          <AvatarFallback>{assignee.userName.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{assignee.userName}</div>
                          <div className="text-xs text-muted-foreground capitalize">{assignee.role ?? 'Auditor'}</div>
                        </div>
                        <StatusBadge status={assignee.status} size="sm" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="border">
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {activityLog.slice(0, 5).map((activity) => (
                      <div key={activity.id} className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Clock className="h-4 w-4 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">{activity.description}</div>
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(activity.timestamp), 'MMM d, h:mm a')}
                          </div>
                        </div>
                      </div>
                    ))}
                    {activityLog.length === 0 && (
                      <div className="text-sm text-muted-foreground text-center py-4">
                        No activity yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Exceptions Tab */}
        <TabsContent value="exceptions">
          <Card className="border">
            <CardHeader>
              <CardTitle>Exceptions ({exceptions.length})</CardTitle>
              <CardDescription>Issues and discrepancies found during this audit</CardDescription>
            </CardHeader>
            <CardContent>
              {exceptions.length > 0 ? (
                <div className="space-y-3">
                  {exceptions.map((exception) => (
                    <button type="button" key={exception.id} className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer w-full text-left bg-transparent" onClick={() => handleViewException(exception)}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{exception.assetName}</span>
                        <Badge variant={exception.severity === 'Critical' ? 'destructive' : 'default'}>
                          {exception.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{exception.exceptionType}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-600" />
                  <p>No exceptions found</p>
                  <p className="text-sm mt-2">All assets are in good condition</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card className="border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Activity Log</CardTitle>
              <CardDescription>Complete history of actions and changes</CardDescription>
            </CardHeader>
            <CardContent>
              {activityLog.length > 0 ? (
                <div className="relative space-y-2">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-2 bottom-2 w-px bg-border" />
                  
                  {activityLog.map((activity, index) => (
                    <div key={activity.id} className="relative flex gap-3 py-2">
                      <div className="flex flex-col items-center relative z-10">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 ring-2 ring-background">
                          <Clock className="h-4 w-4 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div className="flex items-start justify-between gap-2 mb-0.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-sm truncate">{activity.performedBy}</span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {getActivityTypeLabel(activity.activityType)}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">
                            {format(new Date(activity.timestamp), 'MMM d, h:mm a')}
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">{activity.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No activity recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
              </div>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Dialogs */}
      {audit && (
        <>
          <EditAuditDialog
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            audit={audit}
            onSave={handleEditAudit}
          />
          
          <ExportOptionsDialog
            open={showExportDialog}
            onOpenChange={setShowExportDialog}
            onExport={handleExport}
          />
          
          <ConfirmDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            title="Delete Audit"
            description="Are you sure you want to delete this audit? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            variant="destructive"
            onConfirm={handleDeleteAudit}
          />
        </>
      )}

      {/* Exception Details Modal - Temporarily disabled, use ExceptionDetailPanel instead */}
      {/* <ExceptionDetailsModal
        exception={selectedException ? convertToAssetException(selectedException) : null}
        open={showExceptionDialog}
        onOpenChange={setShowExceptionDialog}
        onSave={(exception: any) => {
          console.log('Saving exception:', exception);
          loadAuditDetails();
        }}
        onUpdateStatus={(exceptionId: any, status: any) => {
          handleExceptionStatusChange(exceptionId, status as ExceptionStatus);
        }}
        onCreateWorkItem={(exceptionId: any) => {
          console.log('Creating work item for:', exceptionId);
        }}
        onLinkWorkItem={(exceptionId: any) => {
          console.log('Linking work item for:', exceptionId);
        }}
      /> */}
    </div>
  );
}
